import axios from 'axios';
import { getRequiredRuntimeConfig, PRODUCTION_API_BASE_URL } from './runtimeConfig';

const runtimeConfig = getRequiredRuntimeConfig();
const API_BASE = runtimeConfig.apiBaseUrl;

if (!process.env.REACT_APP_API_URL && !runtimeConfig.isExplicitLocalDevelopment) {
  console.warn(
    `REACT_APP_API_URL is not set. Falling back to production backend ${PRODUCTION_API_BASE_URL}.`
  );
}

// Create axios instance with timeout and retry logic
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('rtb_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('rtb_token');
      localStorage.removeItem('rtb_user');
      window.location.href = '/login';
    }
    
    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        console.error('API Request Timeout - Server not responding', { url: error.config?.url });
      } else if (error.message === 'Network Error') {
        console.error('Network Error - Cannot reach backend API', { 
          apiUrl: API_BASE,
          androidEmulator: navigator.userAgent.toLowerCase().includes('android')
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword, confirmPassword) =>
    api.put('/auth/password', { current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword }),
  completeSupabaseInvite: (supabaseAccessToken) =>
    api.post('/auth/complete-supabase-invite', { supabase_access_token: supabaseAccessToken }),
  updateDeviceToken: (token, platform = 'android', deviceId = null) =>
    api.post('/notifications/device-token', { token, platform, device_id: deviceId }),
};

// Venue APIs
export const venueAPI = {
  list: () => api.get('/venues'),
  get: (id) => api.get(`/venues/${id}`),
  create: (data) => api.post('/venues', data),
  update: (id, data) => api.put(`/venues/${id}`, data),
};

// Checklist Template APIs
export const checklistTemplateAPI = {
  list: (venueId, includeTemplates = false, includeNonLive = false) => api.get('/checklist-templates', { params: { venue_id: venueId, include_templates: includeTemplates, include_non_live: includeNonLive } }),
  get: (id) => api.get(`/checklist-templates/${id}`),
  create: (data) => api.post('/checklist-templates', data),
  update: (id, data) => api.put(`/checklist-templates/${id}`, data),
  delete: (id) => api.delete(`/checklist-templates/${id}`),
  getMasterTemplates: () => api.get('/checklist-templates/masters'),
  clone: (templateId, venueId) => api.post(`/checklist-templates/${templateId}/clone`, null, { params: { venue_id: venueId } }),
  import: (formData) => api.post('/checklist-templates/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  export: (id) => api.get(`/checklist-templates/${id}/export`),
};

// Checklist Submission APIs
export const checklistSubmissionAPI = {
  list: (venueId, limit = 50) => api.get('/checklist-submissions', { params: { venue_id: venueId, limit } }),
  create: (data) => api.post('/checklist-submissions', data),
};

// Task APIs
export const taskAPI = {
  list: (venueId, status) => api.get('/tasks', { params: { venue_id: venueId, status } }),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  create: (data) => api.post('/tasks', data),
};

// Audit APIs
export const auditAPI = {
  list: (venueId) => api.get('/audits', { params: { venue_id: venueId } }),
  create: (data) => api.post('/audits', data),
  getPDF: (id) => api.get(`/audits/${id}/pdf`),
  getPDFBinary: (id) => api.get(`/audits/${id}/pdf/download`, { responseType: 'arraybuffer' }),
  analyse: (data) => api.post('/audit/analyse', data),
  getIntelligenceReport: (auditId) => api.get(`/audit/report/${auditId}`),
  getIntelligenceReportPdfBinary: (auditId) => api.get(`/audit/report/${auditId}/pdf`, { responseType: 'arraybuffer' }),
};

// Holiday Request APIs
export const holidayAPI = {
  list: (venueId) => api.get('/holiday-requests', { params: { venue_id: venueId } }),
  create: (data) => api.post('/holiday-requests', data),
  updateStatus: (id, status) => api.put(`/holiday-requests/${id}/status`, null, { params: { status } }),
};

// Notification APIs
export const notificationAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  registerDeviceToken: (token, platform = 'android', deviceId = null) => api.post('/notifications/device-token', { token, platform, device_id: deviceId }),
  unregisterDeviceToken: (token) => api.delete('/notifications/device-token', { data: { token } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getVenueOps: (siteId) => api.get('/dashboard/venue-ops', { params: { site_id: siteId } }),
  getSiteHealth: (siteId) => api.get('/dashboard/site-health', { params: { site_id: siteId } }),
  getPerformanceInsights: (siteId) => api.get('/analytics/performance', { params: { site_id: siteId } }),
};

// Venue Intelligence APIs
export const intelligenceAPI = {
  createEvent: (data) => api.post('/venue-event', data),
  createExternalEvent: (data) => api.post('/venue-event/external', data),
  listTriggers: (venueId) => api.get('/venue-triggers', { params: { venue_id: venueId } }),
  createTrigger: (data) => api.post('/venue-triggers', data),
  updateTrigger: (triggerId, data) => api.put(`/venue-triggers/${triggerId}`, data),
  deleteTrigger: (triggerId) => api.delete(`/venue-triggers/${triggerId}`),
  getVenueEvents: (venueId, eventType = null, limit = 100) => api.get('/venue-events', { params: { venue_id: venueId, event_type: eventType, limit } }),
  getVenueActionLog: (venueId, limit = 100) => api.get('/venue-action-log', { params: { venue_id: venueId, limit } }),
  getVenueTimeline: (venueId, limit = 300) => api.get(`/venue/${venueId}/timeline`, { params: { limit } }),
  getVenueInsights: (venueId) => api.get('/venue-insights', { params: { venue_id: venueId } }),
  getNetworkInsights: (venueIds) => api.get('/venue-performance/insights', { params: { venue_ids: venueIds } }),
  getManagerScorecards: (venueIds) => api.get('/manager-scorecards', { params: { venue_ids: venueIds } }),
  getSuggestions: () => api.get('/intelligence-suggestions'),
};

// User APIs
export const userAPI = {
  list: () => api.get('/users'),
  listByVenue: (venueId, activeOnly = true) => api.get(`/users/by-venue/${venueId}`, { params: { active_only: activeOnly } }),
  get: (id) => api.get(`/users/${id}`),
  updateRole: (id, role) => api.put(`/users/${id}/role`, null, { params: { role } }),
  updateVenues: (id, venueIds) => api.put(`/users/${id}/venues`, venueIds),
  updatePermissions: (id, permissions) => api.put(`/users/${id}/permissions`, { custom_permissions: permissions }),
  updateProfile: (id, name, email) => api.put(`/users/${id}/profile`, { name, email }),
};

// Department APIs
export const departmentAPI = {
  list: (venueId) => api.get(`/venues/${venueId}/departments`),
  create: (venueId, data) => api.post(`/venues/${venueId}/departments`, data),
  delete: (deptId) => api.delete(`/departments/${deptId}`),
};

// Training APIs
export const trainingAPI = {
  list: (venueId) => api.get('/training-records', { params: { venue_id: venueId } }),
  myList: () => api.get('/me/training-records'),
  grouped: (venueId) => api.get('/training-records-grouped', { params: { venue_id: venueId } }),
  create: (data) => api.post('/training-records', data),
  update: (id, data) => api.put(`/training-records/${id}`, data),
  delete: (id) => api.delete(`/training-records/${id}`),
  getGauge: (venueId) => api.get('/dashboard/rtb-gauge', { params: { venue_id: venueId } }),
};

// Department Assignment APIs
export const assignmentAPI = {
  list: (venueId) => api.get(`/venues/${venueId}/department-assignments`),
  create: (venueId, data) => api.post(`/venues/${venueId}/department-assignments`, data),
  delete: (assignmentId) => api.delete(`/department-assignments/${assignmentId}`),
};

// Invite APIs
export const inviteAPI = {
  list: (venueId) => api.get('/invites', { params: { venue_id: venueId } }),
  send: (data) => api.post('/invites', data),
  cancel: (id) => api.delete(`/invites/${id}`),
  resend: (id) => api.post(`/invites/${id}/resend`),
};

// ── Kiosk Terminal APIs ──────────────────────────────────────────────────────
export const kioskAPI = {
  // Authenticate via 4-digit PIN (venue-scoped)
  pinLogin: (pin, venueId) => api.post('/kiosk/pin-login', { pin, venue_id: venueId }),

  // Ambient data (no-auth, all panels in one request)
  getAmbientData: (venueId) => api.get('/kiosk/ambient', { params: { venue_id: venueId } }),

  // Staff post-PIN panels
  getStaffTasks:    (userId, venueId) => api.get('/kiosk/staff-tasks',    { params: { user_id: userId, venue_id: venueId } }),
  getStaffTraining: (userId, venueId) => api.get('/kiosk/staff-training', { params: { user_id: userId, venue_id: venueId } }),

  // Manager Message Board
  getMessageBoard:  (venueId) => api.get('/kiosk/message-board',  { params: { venue_id: venueId } }),
  setMessageBoard:  (venueId, message) => api.put('/kiosk/message-board', { venue_id: venueId, message }),

  // Stock 86 toggle
  getStock86:       (venueId)          => api.get('/kiosk/stock86',                    { params: { venue_id: venueId } }),
  addStock86Item:   (venueId, name, department) => api.post('/kiosk/stock86',           { venue_id: venueId, name, department }),
  updateStock86:    (venueId, id, available) => api.put(`/kiosk/stock86/${id}`,          { venue_id: venueId, available }),
  removeStock86Item:(venueId, id)       => api.delete(`/kiosk/stock86/${id}`,            { data: { venue_id: venueId } }),

  // Daily specials (OPS Menu Adder)
  getMenuSpecials:  (venueId) => api.get('/kiosk/menu-specials',  { params: { venue_id: venueId } }),
  addMenuSpecial:   (venueId, data)    => api.post('/kiosk/menu-specials',  { venue_id: venueId, ...data }),
  removeMenuSpecial:(venueId, id)      => api.delete(`/kiosk/menu-specials/${id}`, { data: { venue_id: venueId } }),

  // Weather (proxied through backend to protect API key)
  getWeather:       (venueId) => api.get('/kiosk/weather',         { params: { venue_id: venueId } }),

  // Live match alerts
  getLiveMatches:   (venueId) => api.get('/kiosk/live-matches',    { params: { venue_id: venueId } }),

  // Incident reports (kiosk-safe endpoints)
  getIncidents:     (venueId, limit = 20) => api.get('/kiosk/incidents', { params: { venue_id: venueId, limit } }),
  createIncident:   (data) => api.post('/kiosk/incidents', data),

  // EHO Vault (kiosk-safe endpoint)
  generatePassport: (venueId, userId) => api.post('/kiosk/eho/passport', { venue_id: venueId, user_id: userId }),
  getPanicMode: (venueId, userId) => api.post('/kiosk/eho/panic-mode', { venue_id: venueId, user_id: userId }),
  emailPassport: (venueId, userId, toEmail) => api.post('/kiosk/eho/passport/email', { venue_id: venueId, user_id: userId, to_email: toEmail }),
};

// ── EHO Vault / Compliance Passport ─────────────────────────────────────────
export const ehoAPI = {
  // Generate on-demand Compliance Passport PDF (base64 response)
  generatePassport: (venueId) => api.post('/eho/passport', { venue_id: venueId }),
  getPanicMode: (siteId) => api.get('/eho/panic-mode', { params: { site_id: siteId } }),
  emailPassport: (siteId, toEmail) => api.post('/eho/passport/email', { site_id: siteId, to_email: toEmail }),
  generateCompliancePack: (siteId) => api.post('/compliance/pack/generate', { site_id: siteId }),

  // Get the public secure link for a venue's latest compliance passport
  getPublicLink:    (venueId) => api.get(`/eho/passport/${venueId}/link`),

  // Trigger EOD archive + refresh cycle manually
  archiveAndRefresh:(venueId) => api.post(`/eho/passport/${venueId}/archive`),
};

// ── Incident Log API ─────────────────────────────────────────────────────────
export const incidentAPI = {
  list:    (venueId, limit = 50)   => api.get('/incidents',         { params: { venue_id: venueId, limit } }),
  create:  (data)                  => api.post('/incidents',        data),
  get:     (id)                    => api.get(`/incidents/${id}`),
  amend:   (id, data)              => api.post(`/incidents/${id}/amendment`, data),
  getLast7Days: (venueId)          => api.get('/incidents/last-7-days', { params: { venue_id: venueId } }),
};

// EOD handover + carry-over APIs
export const eodAPI = {
  submitHandover: (payload) => api.post('/eod/handover', payload),
  listCarryover: (venueId) => api.get('/eod/carryover', { params: { venue_id: venueId } }),
  resolveCarryover: (id) => api.post(`/eod/carryover/${id}/resolve`),
  submitEndOfDayReport: (payload) => api.post('/reports/end-of-day', payload),
  getTodaysEndOfDayReport: (siteId) => api.get('/reports/end-of-day', { params: { site_id: siteId } }),
};

export const briefingAPI = {
  getTodaysBriefing: (siteId) => api.get('/briefings/today', { params: { site_id: siteId } }),
  submitStartOfDay: (payload) => api.post('/briefings/start-of-day', payload),
};

// Permission capabilities definition
export const CAPABILITIES = [
  {
    id: 'can_create_checklists',
    label: 'Create Checklists',
    description: 'Unlocks the Checklist Builder - allows creating new templates',
    risk: 'low',
    category: 'Builder Access',
  },
  {
    id: 'can_edit_locked_items',
    label: 'Edit Locked Items',
    description: 'Allows editing corporate compliance tasks marked as locked',
    risk: 'high',
    category: 'Compliance Override',
  },
  {
    id: 'can_bypass_geofence',
    label: 'Bypass Geofence',
    description: 'Allows completing checks off-site - useful for testing',
    risk: 'high',
    category: 'Location Override',
  },
  {
    id: 'can_manage_recurring',
    label: 'Manage Recurring Schedules',
    description: 'Allows changing schedule frequencies for checklists',
    risk: 'medium',
    category: 'Scheduling',
  },
  {
    id: 'can_delete_records',
    label: 'Delete Records',
    description: 'High risk: allows deletion of historical logs and templates',
    risk: 'high',
    category: 'Data Management',
  },
  {
    id: 'can_view_cross_venue_data',
    label: 'View Cross-Venue Data',
    description: 'Allows viewing data outside their assigned site',
    risk: 'medium',
    category: 'Data Access',
  },
];

// ── RTB Phase 2: EHO-Shield Compliance Engine APIs ──────────────────────────
export const phase2API = {
  // Document Verification
  createDocument: (data) => api.post('/phase2/documents', data),
  getDocuments: (venueId, documentType) => api.get('/phase2/documents', { params: { venue_id: venueId, document_type: documentType } }),
  updateDocumentReading: (documentId, data) => api.post(`/phase2/documents/${documentId}/read`, data),
  getDocumentReading: (documentId) => api.get(`/phase2/documents/${documentId}/reading`),

  // Knowledge Proofing
  createKnowledgeQuestion: (data) => api.post('/phase2/knowledge/questions', data),
  createKnowledgeAssessment: (data) => api.post('/phase2/knowledge/assessments', data),

  // Regulatory Modules
  createAllergenEntry: (data) => api.post('/phase2/allergens', data),
  createAllergenVerification: (data) => api.post('/phase2/allergens/verifications', data),
  createCOSHHDocument: (data) => api.post('/phase2/coshh', data),
  uploadCoshhSds: (formData) => api.post('/phase2/compliance/COSHH/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  searchCoshhSds: (keyword) => api.get('/phase2/compliance/COSHH/search', { params: { keyword } }),
  createCertification: (data) => api.post('/phase2/certifications', data),
  createMenuItem: (data) => api.post('/phase2/menu-items', data),

  // Enterprise Site Mapping
  createCompany: (data) => api.post('/phase2/companies', data),
  assignRegionalUser: (data) => api.post('/phase2/regional-users', data),

  // Smart Operational Safeguards
  createEHOAuditTemplate: (data) => api.post('/phase2/eho-templates', data),
  addEHOAuditItem: (templateId, data) => api.post(`/phase2/eho-templates/${templateId}/items`, data),
  createPhotoRequirement: (data) => api.post('/phase2/photo-requirements', data),

  // Staff & HR Integration
  createStaffRequest: (data) => api.post('/phase2/staff-requests', data),
  getStaffRequests: (venueId, status) => api.get('/phase2/staff-requests', { params: { venue_id: venueId, status } }),
  updateStaffRequestStatus: (requestId, status, rejectionReason) => api.put(`/phase2/staff-requests/${requestId}/status`, { status, rejection_reason: rejectionReason }),

  // Dashboard & Analytics
  getComplianceScore: (venueId) => api.get(`/phase2/compliance-score/${venueId}`),
  getCertificationAlerts: (daysAhead = 30) => api.get('/phase2/certification-alerts', { params: { days_ahead: daysAhead } }),
  getTrainingAlerts: () => api.get('/phase2/training-alerts'),
};

export default api;
